/**
 * WorkspaceCommHistory — Permanent Communications History for Client & Lead Workspaces.
 *
 * Displays ALL historical conversations for a client regardless of queue_status,
 * including cleared ones.  Staff can search, filter by channel, view full threads,
 * and manually reopen cleared conversations from here.
 */
import { useState, useEffect, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CH_LABEL = { email: "Email", portal: "Secure Portal", webchat: "Website Chat", sms: "SMS", whatsapp: "WhatsApp", system: "Portal", internal: "Internal" };
const CH_ICON = { email: Icons.Mail, portal: Icons.Lock, webchat: Icons.MessageSquare, sms: Icons.MessageCircle, whatsapp: Icons.MessageCircle, system: Icons.Lock };
const STATUS_COLOR = {
  open: "bg-blue-100 text-blue-700",
  waiting_on_staff: "bg-amber-100 text-amber-700",
  waiting_on_client: "bg-slate-100 text-slate-600",
  escalated: "bg-rose-100 text-rose-700",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-slate-100 text-slate-500",
  cleared: "bg-slate-100 text-slate-500",
};

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return d.toLocaleDateString(undefined, { weekday: "short" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function ConvRow({ conv, onClick, active }) {
  const ChIcon = CH_ICON[conv.channel] || Icons.MessageSquare;
  const isCleared = conv.queue_status === "cleared";
  return (
    <button onClick={onClick} data-testid={`wch-conv-row-${conv.id}`}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${active ? "bg-blue-50 border-l-2 border-l-blue-500" : ""}`}>
      <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${isCleared ? "bg-slate-100" : "bg-blue-50"}`}>
        <ChIcon className={`h-4 w-4 ${isCleared ? "text-slate-400" : "text-blue-500"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm font-medium truncate ${isCleared ? "text-slate-500" : "text-slate-800"}`}>
            {conv.contact_name || conv.contact_email || "Unknown Sender"}
          </span>
          <span className="text-[10px] text-slate-400 shrink-0">{fmtDate(conv.last_message_at)}</span>
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">{conv.subject || "(no subject)"}</div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">{CH_LABEL[conv.channel] || conv.channel}</span>
          {isCleared && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">Cleared</span>}
          {!isCleared && conv.conv_status && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLOR[conv.conv_status] || "bg-slate-100 text-slate-500"}`}>
              {conv.conv_status?.replace(/_/g, " ")}
            </span>
          )}
          {conv.unanswered_count > 0 && !isCleared && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-600 font-medium">{conv.unanswered_count} unanswered</span>
          )}
        </div>
      </div>
    </button>
  );
}

function MsgBubble({ msg }) {
  const isOut = msg.direction === "outbound" || msg.sender_type === "staff" || msg.sender_type === "firm";
  const isNote = msg.internal_note;
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${isNote ? "bg-amber-50 border border-amber-200" : isOut ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
        {isNote && <div className="text-[10px] text-amber-600 font-medium mb-1 uppercase tracking-wide">Internal Note</div>}
        <div className="text-sm whitespace-pre-wrap">{msg.body || <em className="text-slate-400">No body</em>}</div>
        <div className={`text-[10px] mt-1.5 ${isNote ? "text-amber-500" : isOut ? "text-white/50" : "text-slate-400"}`}>
          {msg.sender_name || (isOut ? "Staff" : msg.sender_email)} · {fmtDate(msg.sent_at || msg.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function WorkspaceCommHistory({ clientId }) {
  const [loading, setLoading] = useState(false);
  const [convs, setConvs] = useState([]);
  const [channel, setChannel] = useState("all");
  const [qFilter, setQueueFilter] = useState("all");   // "all" | "active" | "cleared"
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reopening, setReopening] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (channel !== "all") params.set("channel", channel);
      if (qFilter !== "all") params.set("queue_status", qFilter);
      if (debouncedSearch) params.set("q", debouncedSearch);
      const { data } = await api.get(`/communications/workspace-history/${clientId}?${params}`);
      setConvs(data.items || []);
    } catch { toast.error("Failed to load communications history"); }
    finally { setLoading(false); }
  }, [clientId, channel, qFilter, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const openThread = async (conv) => {
    setSelected(conv.id);
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/communications/conversations/${conv.id}`);
      setThreadData(data);
    } catch { toast.error("Failed to load conversation thread"); }
    finally { setThreadLoading(false); }
  };

  const reopen = async () => {
    if (!selected) return;
    setReopening(true);
    try {
      const { data } = await api.post(`/communications/conversations/${selected}/reopen`);
      if (data.already_active) { toast.info("Conversation is already active."); }
      else { toast.success("Conversation reopened — it's back in the active Communications Center queue."); load(); setThreadData(d => d ? { ...d, conversation: { ...d.conversation, queue_status: null } } : d); }
    } catch { toast.error("Failed to reopen conversation"); }
    finally { setReopening(false); }
  };

  const selectedConv = convs.find(c => c.id === selected);
  const isCleared = selectedConv?.queue_status === "cleared" || threadData?.conversation?.queue_status === "cleared";

  const channels = [...new Set(convs.map(c => c.channel).filter(Boolean))];

  return (
    <div className="flex h-full min-h-[500px]" data-testid="workspace-comm-history">
      {/* Left pane — conversation list */}
      <div className={`${selected ? "hidden md:flex" : "flex"} flex-col border-r border-slate-200 w-full md:w-80 lg:w-96 shrink-0`}>
        {/* Search & filters */}
        <div className="p-3 border-b border-slate-100 space-y-2">
          <div className="relative">
            <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by subject or sender…"
              data-testid="wch-search" className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={channel} onChange={e => setChannel(e.target.value)} data-testid="wch-channel-filter"
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400 bg-white">
              <option value="all">All Channels</option>
              <option value="email">Email</option>
              <option value="portal">Secure Portal</option>
              <option value="webchat">Website Chat</option>
              {channels.filter(c => !["email","portal","webchat","internal"].includes(c)).map(c =>
                <option key={c} value={c}>{CH_LABEL[c] || c}</option>
              )}
            </select>
            <select value={qFilter} onChange={e => setQueueFilter(e.target.value)} data-testid="wch-status-filter"
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400 bg-white">
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="cleared">Cleared Only</option>
            </select>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="flex items-center justify-center py-12 text-sm text-slate-400"><Icons.Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>}
          {!loading && convs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Icons.MessageSquare className="h-8 w-8 mb-2 opacity-30" />
              <div className="text-sm">No communications history found.</div>
              {(search || channel !== "all" || qFilter !== "all") && <div className="text-xs mt-1 text-slate-400">Try clearing your filters.</div>}
            </div>
          )}
          {!loading && convs.map(conv => (
            <ConvRow key={conv.id} conv={conv} onClick={() => openThread(conv)} active={selected === conv.id} />
          ))}
        </div>

        {/* Summary */}
        {!loading && convs.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate-400">
            {convs.length} conversation{convs.length !== 1 ? "s" : ""} · {convs.filter(c => c.queue_status !== "cleared").length} active · {convs.filter(c => c.queue_status === "cleared").length} cleared
          </div>
        )}
      </div>

      {/* Right pane — thread detail */}
      {selected && (
        <div className="flex-1 flex flex-col min-w-0" data-testid="wch-thread-pane">
          {/* Thread header */}
          <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 bg-white">
            <button onClick={() => { setSelected(null); setThreadData(null); }} className="md:hidden mr-1" data-testid="wch-back">
              <Icons.ArrowLeft className="h-5 w-5 text-slate-500" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-800 truncate">{selectedConv?.subject || "(no subject)"}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {selectedConv?.contact_name || selectedConv?.contact_email} · {CH_LABEL[selectedConv?.channel] || selectedConv?.channel}
                {isCleared && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-500 font-medium">Cleared</span>}
              </div>
            </div>
            {isCleared && (
              <button onClick={reopen} disabled={reopening} data-testid="wch-reopen-btn"
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-3 py-1.5 disabled:opacity-50 shrink-0">
                {reopening ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.RefreshCw className="h-4 w-4" />}
                Reopen Conversation
              </button>
            )}
            {!isCleared && (
              <a href={`/communications?conversation=${selected}`} data-testid="wch-open-in-cc"
                className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 shrink-0">
                <Icons.ExternalLink className="h-3.5 w-3.5" />Open in Comms
              </a>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50" data-testid="wch-messages">
            {threadLoading && <div className="flex items-center justify-center py-12 text-sm text-slate-400"><Icons.Loader2 className="h-5 w-5 animate-spin mr-2" />Loading thread…</div>}
            {!threadLoading && threadData && (
              <>
                {(threadData.messages || []).length === 0 && <div className="text-sm text-slate-400 text-center py-10">No messages in this conversation.</div>}
                {(threadData.messages || []).map(msg => <MsgBubble key={msg.id} msg={msg} />)}
              </>
            )}
          </div>

          {/* Attachments strip (if any) */}
          {!threadLoading && threadData?.documents?.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-200 bg-white" data-testid="wch-attachments-strip">
              <div className="text-xs font-medium text-slate-500 mb-2">Attachments ({threadData.documents.length})</div>
              <div className="flex items-center gap-2 flex-wrap">
                {threadData.documents.map((d, i) => (
                  <div key={d.id || i} className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 rounded px-2.5 py-1">
                    <Icons.Paperclip className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="truncate max-w-[120px]">{d.display_filename || d.original_filename || "Attachment"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Read-only footer note */}
          <div className="px-5 py-2.5 border-t border-slate-100 bg-white text-xs text-slate-400 flex items-center gap-2">
            <Icons.History className="h-3.5 w-3.5" />
            {isCleared
              ? "This conversation has been cleared. Use Reopen Conversation to return it to the active Communications Center."
              : "Historical view — composing is available in the Communications Center."}
          </div>
        </div>
      )}
    </div>
  );
}
